import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { safeLocalStorage } from '@/lib/storage-utils';

export type DashboardMode = 'agency' | 'crm';

interface DashboardContextValue {
  currentDashboard: DashboardMode;
  switchDashboard: (mode: DashboardMode) => void;
}

const DashboardContext = createContext<DashboardContextValue | undefined>(undefined);

const STORAGE_KEY = 'activeDashboard';

// Routes that belong to the CRM dashboard
const CRM_ROUTE_PREFIXES = ['/crm', '/services', '/deals', '/tickets', '/forms', '/proposals', '/invoices'];

// Routes that belong to the Agency dashboard
const AGENCY_ROUTE_PREFIXES = ['/home', '/inbox', '/files', '/brand', '/boards', '/task-board', '/sites', '/calendar', '/knowledge-base'];

function detectDashboardFromPath(pathname: string): DashboardMode | null {
  if (CRM_ROUTE_PREFIXES.some(prefix => pathname.startsWith(prefix))) {
    return 'crm';
  }
  if (AGENCY_ROUTE_PREFIXES.some(prefix => pathname.startsWith(prefix))) {
    return 'agency';
  }
  return null;
}

export const DashboardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [currentDashboard, setCurrentDashboard] = useState<DashboardMode>(() => {
    // First, try to detect from the current URL
    const detected = detectDashboardFromPath(window.location.pathname);
    if (detected) return detected;

    // Then fall back to saved preference
    const saved = safeLocalStorage.getItem(STORAGE_KEY);
    if (saved === 'agency' || saved === 'crm') return saved;

    return 'agency';
  });

  // Auto-detect dashboard from URL changes (e.g., deep links, bookmarks)
  useEffect(() => {
    const detected = detectDashboardFromPath(location.pathname);
    if (detected && detected !== currentDashboard) {
      setCurrentDashboard(detected);
      safeLocalStorage.setItem(STORAGE_KEY, detected);
    }
  }, [location.pathname]);

  const switchDashboard = useCallback((mode: DashboardMode) => {
    if (mode === currentDashboard) return;
    setCurrentDashboard(mode);
    safeLocalStorage.setItem(STORAGE_KEY, mode);

    // Navigate to the default home page for the target dashboard
    if (mode === 'crm') {
      navigate('/crm/home');
    } else {
      navigate('/home');
    }
  }, [currentDashboard, navigate]);

  return (
    <DashboardContext.Provider value={{ currentDashboard, switchDashboard }}>
      {children}
    </DashboardContext.Provider>
  );
};

export function useDashboard() {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
}
